import { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { coverageLabel } from 'utils/vacancyStatsFormat';
import { BoxProps } from '@mui/system';

// Общая палитра дашборда (берём из theme.palette, чтобы графики совпадали с UI).
export const CHART = {
  primary: '#6558F5',
  primaryLight: '#A49EEB',
  primaryDark: '#3E2FEA',
  accepted: '#54D62C',
  pending: '#FFC107',
  expired: '#919EAB',
  danger: '#F94C83'
} as const;

/** Оттенки воронки сверху вниз: Приглашён → Извлечение → Интервью → Завершил. */
export const FUNNEL_COLORS = ['#A49EEB', '#8678F0', '#6558F5', '#3E2FEA', '#EB2F6B'] as const;

/** Карточка-обёртка виджета — единый стиль с остальными HR-страницами. */
export const widgetCardSx = {
  p: 3,
  borderRadius: 3,
  height: '100%',
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'grey.300',
  marginLeft: '12px !important',
  width: '100%'
} as const;

export const Overline = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      fontWeight: 700,
      fontSize: 12,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      color: 'text.disabled',
      mb: 0.5
    }}
  >
    {children}
  </Typography>
);

interface WidgetCardProps {
  overline: string;
  title: string;
  /** Подпись покрытия «оценено N из M» — показываем у scores/psychometrics/price. */
  coverage?: { evaluated: number; ofTotal: number };
  action?: ReactNode;
  children: ReactNode;
  sx?: BoxProps['sx'];
}

export const WidgetCard = ({
  overline,
  title,
  coverage,
  action,
  children,
  sx
}: WidgetCardProps) => (
  <Box sx={{ ...widgetCardSx, ...sx }}>
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={2}
      sx={{ mb: 0 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Overline>{overline}</Overline>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'grey.900', lineHeight: 1.3, mb: 0 }}>
          {title}
        </Typography>
        {coverage && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
          >
            {coverageLabel(coverage.evaluated, coverage.ofTotal)}
          </Typography>
        )}
      </Box>
      {action}
    </Stack>
    {children}
  </Box>
);

/** Пустое состояние блока (например, нет завершённых интервью с результатом). */
export const EmptyState = ({
  text = 'Пока нет завершённых интервью с результатом',
  icon = 'mdi:chart-box-outline'
}: {
  text?: string;
  icon?: string;
}) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    spacing={1}
    sx={{ py: 5, textAlign: 'center' }}
  >
    <Icon icon={icon} width={40} height={40} color="#919EAB" />
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Stack>
);

interface KpiTileProps {
  label: string;
  value: string;
  hint?: string;
  emphasis?: boolean;
}

/** Крупный KPI-тайл (avg / median и т.п.). */
export const KpiTile = ({ label, value, hint, emphasis = false }: KpiTileProps) => (
  <Box
    sx={{
      p: 2,
      height: '100%',
      borderRadius: 2,
      bgcolor: emphasis ? 'primary.lighter' : 'grey.100',
      border: '1px solid',
      borderColor: emphasis ? 'primary.light' : 'grey.300'
    }}
  >
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: 'text.disabled',
        mb: 0.5
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h4"
      sx={{ fontWeight: 700, lineHeight: 1.1, color: emphasis ? 'primary.dark' : 'grey.900' }}
    >
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

/** Маленький «пилюлей» индикатор-метрика (всего / аннулировано / конверсии). */
export const StatPill = ({
  label,
  value,
  color = 'text.primary'
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <Box
    sx={{
      px: 1.75,
      py: 1,
      borderRadius: 2,
      bgcolor: 'grey.100',
      border: '1px solid',
      borderColor: 'grey.300',
      minWidth: 96
    }}
  >
    <Typography
      variant="caption"
      sx={{ display: 'block', color: 'text.disabled', fontWeight: 700, fontSize: 11 }}
    >
      {label}
    </Typography>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color, lineHeight: 1.2 }}>
      {value}
    </Typography>
  </Box>
);
